import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import Header from '../../components/common/Header';
import Sidebar from '../../components/common/Sidebar';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  FolderGit2, Bot, Send, User, ArrowLeft,
  ShieldAlert, BookOpen
} from 'lucide-react';
import './ProjectDetails.css';

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { employee } = useAuth();

  const deptAvatar = employee?.department_detail?.avatar_image;
  const deptName = employee?.department_detail?.name;

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  // Chat State
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchProjectDetails();
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      setForbidden(false);
      const res = await api.get(`/projects/${id}/`);
      setProject(res.data);

      // Initial AI greeting for this project
      setMessages([{
        id: 'welcome',
        sender: 'AI',
        message: `Welcome to ${res.data.name}! I am your AI Project Mentor. Ask me any question about the architecture, workflow, APIs, or coding standards for this project.`,
        sources: []
      }]);

    } catch (err) {
      console.error(err);
      if (err.response && err.response.status === 403) {
        setForbidden(true);
      } else {
        addToast('Failed to load project details.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || chatLoading) return;

    const userQ = input.trim();
    setInput('');

    setMessages(prev => [...prev, { id: Date.now(), sender: 'USER', message: userQ }]);
    setChatLoading(true);

    try {
      const res = await api.post(`/ai/project/${id}/`, {
        session_id: sessionId,
        question: userQ
      });

      if (res.data.session_id) {
        setSessionId(res.data.session_id);
      }

      const aiMsg = {
        id: res.data.message?.id || Date.now() + 1,
        sender: 'AI',
        message: res.data.answer,
        sources: res.data.sources || []
      };

      setMessages(prev => [...prev, aiMsg]);
    } catch (err) {
      console.error(err);
      if (err.response && err.response.status === 403) {
        addToast('403 Forbidden: You are not assigned to this project.', 'error');
        setMessages(prev => [...prev, {
          id: Date.now() + 2,
          sender: 'AI',
          message: '403 Forbidden: You are not assigned to this project. AI access denied.',
          sources: []
        }]);
      } else {
        addToast('Failed to retrieve project AI response.', 'error');
      }
    } finally {
      setChatLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-layout-root">
        <Header />
        <div className="page-body-wrapper">
          <Sidebar />
          <div style={{ flex: 1, padding: '40px' }}><LoadingSpinner label="Loading project details..." /></div>
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="page-layout-root">
        <Header />
        <div className="page-body-wrapper">
          <Sidebar />
          <main style={{ flex: 1, padding: '40px', maxWidth: '700px', margin: '0 auto', width: '100%' }}>
            <div className="card project-detail-forbidden-card">
              <ShieldAlert size={48} className="project-detail-forbidden-icon" />
              <h2 className="project-detail-forbidden-title">403 Forbidden — Access Denied</h2>
              <p className="project-detail-forbidden-desc">
                You are not currently assigned to this project repository. Employee access and AI mentor privileges are strictly limited to assigned projects.
              </p>
              <button className="btn btn-secondary" onClick={() => navigate('/my-projects')}>
                <ArrowLeft size={16} /> Return to My Assigned Projects
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="page-layout-root">
      <Header />
      <div className="page-body-wrapper">
        <Sidebar />
        <main className="page-main-content">
          
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/my-projects')} style={{ marginBottom: '20px' }}>
            <ArrowLeft size={14} /> Back to My Projects
          </button>

          {/* Title Header */}
          <div className="project-detail-header-row">
            <div className="project-detail-icon-box">
              <FolderGit2 size={26} />
            </div>
            <div>
              <h1 className="project-detail-title">{project.name}</h1>
              <p className="project-detail-subtitle">{project.short_description}</p>
            </div>
          </div>

          {/* Main 2-Column Grid */}
          <div className="project-detail-columns">
            
            {/* Left Column: Workflow & Documents */}
            <div className="project-detail-left-column">
              
              {/* Project Workflow Rich Text Box */}
              <div className="card project-detail-workflow-card">
                <h2 className="project-detail-section-title">
                  <BookOpen size={20} style={{ color: '#4f46e5' }} /> Project Workflow & Specifications
                </h2>
                <div
                  className="project-detail-workflow-html"
                  dangerouslySetInnerHTML={{ __html: project.project_workflow || '<p>No workflow documentation provided yet.</p>' }}
                />
              </div>



            </div>

            {/* Right Column: Embedded Project AI Mentor Chat */}
            <div className="card project-detail-chat-card">
              
              {/* Chat Header */}
              <div className="project-detail-chat-header">
                <div className="project-detail-chat-icon-box">
                  {deptAvatar ? (
                    <img src={deptAvatar} alt="AI Lead" className="project-detail-chat-avatar-img" />
                  ) : (
                    <Bot size={20} />
                  )}
                </div>
                <div>
                  <h3 className="project-detail-chat-title">
                    {deptName ? `${deptName} AI Lead (${project.name})` : `Ask MentorAI about ${project.name}`}
                  </h3>
                  <span className="project-detail-chat-tag">
                    Restricted to {project.name} docs
                  </span>
                </div>
              </div>

              {/* Messages Area */}
              <div className="project-detail-chat-scroll">
                {messages.map((msg, index) => {
                  const isUser = msg.sender === 'USER';
                  return (
                    <div key={msg.id || index} className={`project-detail-chat-msg-row ${isUser ? 'user' : 'ai'}`}>
                      <div className={`project-detail-chat-avatar ${isUser ? 'user' : 'ai'}`}>
                        {isUser ? (
                          <User size={16} />
                        ) : deptAvatar ? (
                          <img src={deptAvatar} alt="AI Lead" className="project-detail-chat-avatar-img" />
                        ) : (
                          <Bot size={16} />
                        )}
                      </div>
                      <div className={`project-detail-chat-bubble ${isUser ? 'user' : 'ai'}`}>
                        {msg.message}
                        {!isUser && msg.sources && msg.sources.length > 0 && (
                          <div style={{ marginTop: '8px', paddingTop: '6px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {msg.sources.map((s, i) => (
                              <span key={i} className="badge badge-info" style={{ fontSize: '0.62rem' }}>{s}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {chatLoading && (
                  <div style={{ fontSize: '0.8rem', color: '#64748b', fontStyle: 'italic', padding: '8px' }}>
                    MentorAI searching project documents...
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat Input */}
              <form onSubmit={handleSend} className="project-detail-chat-form">
                <input
                  type="text"
                  className="form-input project-detail-chat-input"
                  placeholder={`Ask about ${project.name}...`}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={chatLoading}
                />
                <button type="submit" className="btn btn-primary btn-sm" disabled={chatLoading || !input.trim()}>
                  <Send size={16} />
                </button>
              </form>

            </div>

          </div>

        </main>
      </div>
    </div>
  );
};

export default ProjectDetails;
